import { Typography, Box } from "@mui/material";
import { useRefCodeLookup } from "@/hooks/useRefCodeLookup";
import type { RefCodeType } from "@/hooks/useRefCodeSearch";

export interface CodeDescriptionLineProps {
  label: string;
  codeType: RefCodeType;
  code: string | undefined | null;
}

/**
 * Renders a compact `label: code — description` line, for enriching read-only
 * claim item views with human-readable code meanings.
 *
 * Graceful by design: if `code` is empty or the ref-code lookup returns no
 * match (or errors), this component renders nothing at all — the page layout
 * stays unaffected when ref data is absent.
 */
export const CodeDescriptionLine = ({ label, codeType, code }: CodeDescriptionLineProps) => {
  const { description } = useRefCodeLookup(codeType, code);
  if (!code || !description) return null;

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}:
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
        {code}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        — {description}
      </Typography>
    </Box>
  );
};
